/**
 * Owner-approved legal texts, stored verbatim as Markdown. Updated
 * 15 October 2026 with the owner-verified company details (Dede Foods
 * Limited, CRO 820134), the customer-facing venue address and the Privacy
 * Policy link. {{CRO}} renders CRO_NUMBER.
 *
 * The VERSION strings are recorded against every booking (server-side). When
 * a text changes, bump its version here AND in supabase/functions/_shared/legal.ts.
 */

export const CRO_NUMBER = "820134"; // owner-verified 15 October 2026
export const CRO_PLACEHOLDER = "[to be confirmed]";

export const TERMS_VERSION = "terms-2026-10-15";
export const REFUND_POLICY_VERSION = "refund-2026-10-15";

export const TERMS_PATH = "/terms";
export const REFUND_POLICY_PATH = "/refund-policy";
export const PRIVACY_PATH = "/privacy";

export const COMPANY = {
  legalName: "Dede Foods Limited",
  croNumber: CRO_NUMBER,
  tradingName: "The Grey Wolf",
  email: "thegreywolf2026@gmail.com",
  website: "https://thegreywolf.ie",
};

/** Customer-facing trading venue (NOT the registered office, which is on the CRO register). */
export const VENUE_ADDRESS_LINES = ["The Grey Wolf", "Inside Lyster's Pub", "236/238 Harold's Cross Road", "Dublin 6W, D6W RF44", "Ireland"];

export const termsMarkdown = `# The Grey Wolf — Terms & Conditions

**Last updated: 15 October 2026**

## 1. About Us

The Grey Wolf is a trading name of **Dede Foods Limited** ("we", "us" or "our").

Dede Foods Limited — Company Registration Number: {{CRO}}

Venue address: The Grey Wolf, Inside Lyster's Pub, 236/238 Harold's Cross Road, Dublin 6W, D6W RF44, Ireland.

Email: thegreywolf2026@gmail.com

Website: https://thegreywolf.ie

These Terms & Conditions apply to the use of our website, purchases of event tickets, and enquiries or reservations made through our website.

## 2. Event Tickets and Bookings

2.1. Event dates, starting times, ticket prices, inclusions and availability are displayed on the relevant event page.

2.2. All prices are displayed in euro (€). The total amount payable is shown before payment.

2.3. A booking is confirmed only after successful payment has been verified and a booking confirmation has been issued.

2.4. Submitting booking details or starting the payment process does not guarantee a ticket.

2.5. Places may be temporarily reserved during checkout. If payment is not completed within the stated time, the reservation may expire and the places may be released.

2.6. Customers must provide accurate contact information. Booking confirmations and important event updates may be sent to the email address supplied.

2.7. Customers should retain their booking reference and present it when requested at the venue.

## 3. Payments

3.1. Online event ticket payments are processed securely through SumUp.

3.2. We do not store customers' full payment card details on our website.

3.3. A ticket is not considered paid until the payment provider confirms successful payment.

3.4. Any payment processing error, duplicate charge or incorrect charge should be reported promptly to thegreywolf2026@gmail.com.

## 4. Customer Cancellations

4.1. **Event tickets are non-refundable when a customer voluntarily cancels, changes their mind, or decides not to attend.**

4.2. Failure to attend an event does not normally entitle the customer to a refund.

4.3. Tickets for leisure events scheduled for a specific date or period are generally exempt from the standard 14-day online cancellation right under applicable Irish consumer law.

4.4. This policy does not exclude or restrict any statutory consumer rights. Refunds or other remedies will be provided where required by applicable law.

4.5. Requests relating to exceptional circumstances may be considered at our discretion, without creating an automatic entitlement to a refund.

## 5. Event Cancellation or Changes by Us

5.1. We may need to change an event because of circumstances affecting its operation, including performer availability, safety concerns or circumstances beyond our reasonable control.

5.2. If we cancel an event and do not provide the contracted service, customers will be entitled to a refund of the relevant ticket price, subject to applicable law.

5.3. If an event is postponed or materially changed, we will notify affected customers where reasonably possible and explain the available options, including any refund rights required by law.

5.4. Minor changes that do not materially affect the event will not automatically entitle customers to a refund, subject to statutory rights.

5.5. We are not responsible for customers' separately arranged travel, accommodation or other expenses, except where liability cannot lawfully be excluded.

## 6. Admission and Conduct

6.1. Customers must comply with reasonable venue rules and lawful instructions from staff.

6.2. We may refuse admission or require a person to leave where reasonably necessary for safety, unlawful conduct, harassment or serious disruption.

6.3. Alcohol is supplied separately by the licensed venue, where applicable, and is subject to Irish licensing law and age verification.

6.4. Purchasing an event ticket does not automatically include alcoholic beverages unless explicitly stated in the ticket description.

6.5. Any admission restrictions, including age restrictions, will be stated on the relevant event page where applicable.

## 7. Food, Allergens and Dietary Requirements

7.1. Event menus and food inclusions will be described on the relevant event page.

7.2. Customers with food allergies, intolerances or dietary requirements should contact us before purchasing tickets or attending.

7.3. Allergen information is available on request. We will provide accurate information about ingredients and allergens as required by applicable food law.

7.4. We cannot guarantee that food is completely free from traces of an allergen where cross-contact is possible.

## 8. Table Reservations and Enquiries

8.1. Submitting a table booking, catering, private event or general enquiry through our website does not create a confirmed reservation.

8.2. Our team will review the request and contact the customer to confirm availability and any applicable arrangements.

8.3. Any deposit, cancellation fee or special conditions for a private event must be clearly communicated and agreed separately before becoming binding.

## 9. Website Information

9.1. We aim to keep website information accurate and up to date.

9.2. We may update menus, opening hours, event information and website content.

9.3. Changes to these Terms will not retrospectively remove rights already acquired under an existing booking or applicable law.

## 10. Privacy

Personal information is processed in accordance with our Privacy Policy (https://thegreywolf.ie/privacy) and applicable data protection law.

Payment information is handled by our payment provider. We use booking details to manage reservations, process transactions and communicate essential booking information.

## 11. Liability and Consumer Rights

Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot lawfully be excluded or limited.

Nothing in these Terms affects customers' mandatory rights under Irish consumer protection law.

## 12. Governing Law

These Terms are governed by the laws of Ireland, without prejudice to any mandatory consumer protections that may apply.

## 13. Contact

For questions about bookings, payments, cancellations or these Terms, contact:

**The Grey Wolf — Dede Foods Limited**

Dede Foods Limited — Company Registration Number: {{CRO}}

The Grey Wolf
Inside Lyster's Pub
236/238 Harold's Cross Road
Dublin 6W, D6W RF44
Ireland

thegreywolf2026@gmail.com

https://thegreywolf.ie`;

export const refundPolicyMarkdown = `# The Grey Wolf — Refund & Cancellation Policy

**Last updated: 15 October 2026**

This policy applies to event tickets purchased through https://thegreywolf.ie.

The Grey Wolf is a trading name of Dede Foods Limited, Ireland.

Dede Foods Limited — Company Registration Number: {{CRO}}

## 1. Customer-Initiated Cancellations

**All event ticket sales are final and non-refundable if the customer voluntarily cancels, changes their mind or is unable to attend.**

This applies regardless of how far in advance the customer contacts us.

Tickets purchased for leisure events taking place on a specified date or during a specified period are generally excluded from the standard 14-day online cancellation right under Section 111 of the Consumer Rights Act 2022.

This does not affect any statutory consumer rights.

## 2. Non-Attendance

If a customer does not attend an event, no refund will normally be issued.

## 3. Event Cancellation by The Grey Wolf

If we cancel an event and the contracted service is not provided, customers will receive a refund of the ticket price paid, in accordance with applicable consumer law.

We will contact affected customers using their booking contact details.

## 4. Postponements and Material Changes

If an event is postponed or materially changed, we will notify affected customers where reasonably possible.

Available remedies, including refunds where required by law, will depend on the circumstances of the change.

A change that is minor and does not materially alter the contracted event will not automatically entitle a customer to a refund.

## 5. Duplicate or Incorrect Payments

If a customer believes they have been charged twice or charged an incorrect amount, they should email us with their booking reference and transaction details.

Verified payment errors will be corrected and any amounts wrongly charged will be refunded.

## 6. Refund Processing

Approved refunds will normally be processed through SumUp to the original payment method.

Once a refund has been processed, the time taken for the funds to appear in the customer's account depends on the payment provider and the customer's bank.

We do not guarantee a specific bank processing time.

## 7. Exceptional Circumstances

Customers may contact us to explain exceptional circumstances preventing attendance.

We may consider a goodwill refund or alternative arrangement at our discretion. Such consideration does not guarantee a refund and does not affect statutory rights.

## 8. Private Events and Catering

This event ticket refund policy does not automatically apply to private event bookings, catering agreements or ordinary table enquiries.

Any applicable deposits, cancellation conditions or other charges for those services will be communicated and agreed separately.

## 9. How to Contact Us

For cancellation enquiries, payment issues or refund requests, email:

**thegreywolf2026@gmail.com**

Please include your booking reference, event name, event date and a brief explanation of your request.

**The Grey Wolf — Dede Foods Limited**

The Grey Wolf
Inside Lyster's Pub
236/238 Harold's Cross Road
Dublin 6W, D6W RF44
Ireland

https://thegreywolf.ie

## 10. Statutory Rights

Nothing in this policy excludes, limits or overrides rights that customers have under applicable Irish consumer law.`;
